import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";

import {
    comma,
    dateFormatDateTime,
    dateFormatToISOString,
    formatDecimalInput,
    formatNumberWithCommas,
    uncomma,
    useFormInput,
    formatDate,
} from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import {
    useGetCarClassQuery,
    useGetCarGubunQuery,
    useGetCarManufacturerQuery,
    useGetCarModelDetailQuery,
    useGetCarModelQuery,
    useGetCarTrimQuery,
    useGetFuelListQuery,
} from "@/redux/metaApi";
import { useGetBranchInfoQuery, useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import { useGetInsuranceCorpQuery } from "@/redux/insuranceCorpApi";
import { useGetCarLocationListQuery } from "@/redux/carLocationApi";
import { useGetParkingLotListQuery, usePostParkingLotMutation } from "@/redux/parkingLotApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import {
    capitalCorpOptions,
    contractMonthOptions,
    daeinPay1Options,
    daeinPay2Options,
    daemulPayOptions,
    displacementOptions,
    emergencyServiceOptions,
    fuelRateOptions,
    insuranceAgeOptions,
    jasonOptions,
    noInsInjuryOptions,
    originOptions,
    selfCarDamageAmountOptions,
    selfCarDamageRateOptions,
    selfCarDamageTypeOptions,
    transmissionOptions,
    vehicleStatusOptions,
    washStatusOptions,
} from "@/constants/selectOptions";
import {
    useGetCarByIdAdminQuery,
    useLazySearchCarAdminQuery,
    usePostCarAdminMutation,
    usePutCarAdminMutation,
} from "../../redux/adminCarApi";

const INSURANCE_ITEM_CONFIGS = {
    DAEIN_PAY_1: {
        label: "대인 배상 I",
        coverageOptions: daeinPay1Options,
    },
    DAEIN_PAY_2: {
        label: "대인 배상 II",
        coverageOptions: daeinPay2Options,
    },
    DAEMUL_PAY: {
        label: "대물 배상",
        coverageOptions: daemulPayOptions,
    },
    JASON_DEAD: {
        label: "자동차상해(사망)",
        coverageOptions: jasonOptions,
    },
    JASON_INJURY: {
        label: "자동차상해(부상)",
        coverageOptions: jasonOptions,
    },
    JASON_BLOCK: {
        label: "자동차상해(장해)",
        coverageOptions: jasonOptions,
    },
    SELF_CAR_DAMAGE: {
        label: "자기차량손해",
        // 첫 번째 select: '금액'/'비율'
        // 두 번째 select는 첫 번째 선택값에 따라 selfCarDamageAmountOptions / selfCarDamageRateOptions
    },
    NO_INS_INJURY: {
        label: "무보험차상해",
        coverageOptions: noInsInjuryOptions,
    },
    EMERGENCY_SERVICE: {
        label: "긴급출동",
        coverageOptions: emergencyServiceOptions,
    },
};

export const AdminVehicleRegister = () => {
    const { vehicleId } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        isLoading: false,
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,
        // postCar API 필드
        emeyeId: null,
        rentCorpId: null,
        branchId: null,
        parkingLotId: null,
        carLocationId: null,
        ownerName: "",
        carNumber: "",
        vehicleOrigin: "",
        carGubun: "",
        manufacturer: "",
        model: "",
        detailModel: "",
        carClass: "",
        carTrim: "",
        fuel: "",
        transmission: "",
        motorType: "",
        bmNo: undefined,
        bmName: "",
        boiNo: undefined,
        boiName: "",
        boNo: undefined,
        boName: "",
        bsNo: undefined,
        bsName: "",
        bdNo: undefined,
        bdName: "",
        year: 0,
        color: "",
        displacement: 0,
        mileage: 0,
        newPrice: "",
        gasAmount: "",
        washStatus: "",
        vin: "",
        carLocationName: "",
        parkingLotStatus: "" /* "DIRECT", "SELECT" */,
        parkingLotAddYn: "N",
        parkingLotName: "",
        options: [],
        parkingLotBCode: "",
        parkingLotAddress: "",
        parkingLotAddressDetail: "",
        memo: "",
        inspectRangeFrom: "",
        inspectRangeTo: "",
        firstDate: "",
        carStatus: "PENDING",
        capital: {
            capitalCorpName: "", // 캐피탈 업체명
            loanTotalAmount: null, // 대출 총액
            loanInterestRate: null, // 적용 금리
            installmentRange: null, // 할부 기간 (월)
            installmentMonthlyFee: null, // 월 할부금
        },
        insurance: {
            insuranceCorpId: null,
            insMinAge: 0,
            expireDate: "",
            insuranceItems: [
                {
                    insuranceItemType: "DAEIN_PAY_1", // 대인배상1
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "DAEIN_PAY_2", // 대인배상2
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "DAEMUL_PAY", // 대물배상
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "NO_INS_INJURY", // 무보험차상해
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "JASON_DEAD", // 자동차상해(사망)
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "JASON_INJURY", // 자동차상해(부상)
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "JASON_BLOCK", // 자동차상해(장해)
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
                {
                    insuranceItemType: "SELF_CAR_DAMAGE", // 자기차량손해
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                    selfCarDamageRangeType: null, // "PRICE" or "RATE" 선택값
                    selfCarDamageRateType: null,
                },
                {
                    insuranceItemType: "EMERGENCY_SERVICE", // 긴급출동
                    joinYn: "N",
                    limitType: null,
                    compensationRange: null,
                    selfPay: null,
                },
            ],
        },
        carUploads: [
            {
                uploadType: "REGI",
                uploadId: null,
            },
            {
                uploadType: "INSURANCE",
                uploadId: null,
            },
            {
                uploadType: "ESTIMATE",
                uploadId: null,
            },
        ],
        regiFileName: "",
        insuranceFileName: "",
        estimateFileName: "",
    });
    const { mode, isLoading, toastMsg, confirmStatus, addressPopupStatus, id } = formData;

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    const {
        data: carDetailData,
        isLoading: isCarDetailLoading,
        isFetching: isCarDetailFetching,
        error: carDetailError,
    } = useGetCarByIdAdminQuery(vehicleId, {
        skip: !vehicleId,
    });

    // 모드 설정: vehicleId 있으면 "UPDATE"
    useEffect(() => {
        if (vehicleId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: vehicleId,
            }));
        }
    }, [vehicleId, setFormData]);

    // 차량 상세조회 API 데이터 매핑
    useEffect(() => {
        if (carDetailData && carDetailData.data) {
            const response = carDetailData.data;
            console.log(response);

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                ...response,
                mileage: comma(response.mileage),
                newPrice: comma(response.newPrice),
            }));
        }
    }, [carDetailData, setFormData]);

    // API 호출
    const [postCar, { isLoading: isPostLoading }] = usePostCarAdminMutation();
    const [putCar, { isLoading: isPutLoading }] = usePutCarAdminMutation();
    const [postParkingLot] = usePostParkingLotMutation();
    const [doSearchCar, { data: searchCarData, isFetching, error }] = useLazySearchCarAdminQuery();

    const handleSearchCar = async () => {
        if (!formData?.ownerName || !formData?.carNumber) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "소유주와 차량번호를 입력해주세요.",
            }));
            return;
        }
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        const res = await doSearchCar({ car_number: formData?.carNumber, owner_name: formData?.ownerName });

        if (res.data) {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                toastMsg: "차량 조회 완료",
                emeyeId: res?.data.data.emeyeId,
                carGubun: res?.data.data.carGubun,
                manufacturer: res?.data.data.bmNo,
                bmNo: res?.data.data.bmNo,
                bmName: res?.data.data.bmName,
                boiNo: res?.data.data.boiNo,
                boiName: res?.data.data.boiName,
                boNo: res?.data.data.boNo,
                boName: res?.data.data.boName,
                bsNo: res?.data.data.bsNo,
                bsName: res?.data.data.bsName,
                bdNo: res?.data.data.bdNo,
                bdName: res?.data.data.bdName,
                year: res?.data.data.year,
                color: res?.data.data.color,
                fuel: res?.data.data.fuel,
                displacement: res?.data.data.displacement,
                mileage: res?.data.data.mileage,
                motorType: res?.data.data.motorType,
                vin: res?.data.data.vin,
                options: res?.data.data.options,
                firstDate: res?.data.data.firstDate,
                newPrice: res?.data.data.newPrice,
            }));
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 성공",
                isLoading: false,
            }));
        } else {
            console.log(res.error);
            console.log(res.data);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 실패",
                isLoading: false,
            }));
        }
    };

    const buildRequestBody = () => {
        let reqBody = {
            ...formData,
            mileage: Number(uncomma(formData?.mileage)),
            displacement: Number(uncomma(formData?.displacement)),
            newPrice: Number(uncomma(formData?.newPrice)),
            carLocationId: formData?.carLocationId,
            insurance: {
                ...formData?.insurance,
                regInsurancePrice: Number(uncomma(formData?.insurance.regInsurancePrice)),
            },
            capital: {
                ...formData?.capital,
                loanTotalAmount: Number(uncomma(formData?.capital.loanTotalAmount)),
                loanInterestRate: Number(formData?.capital.loanInterestRate),
                installmentMonthlyFee: Number(uncomma(formData?.capital.installmentMonthlyFee)),
                preChargeFee: Number(uncomma(formData?.capital.preChargeFee)),
            },
        };

        if (formData?.parkingLotStatus === "DIRECT" && formData?.parkingLotAddYn) {
            reqBody = {
                ...reqBody,
                parkingLotAddYn: formData?.parkingLotAddYn ? "Y" : "N",
                parkingLotBCode: formData?.parkingLotBCode,
                parkingLotAddress: formData?.parkingLotAddress,
                parkingLotAddressDetail: formData?.parkingLotAddressDetail,
            };
        }
        return reqBody;
    };

    const validateCarRegistration = () => {
        // 1. 사업자 (rentCorpId)
        if (!formData?.rentCorpId) {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자를 선택해주세요." }));
            return false;
        }
        // 2. 소유주 (ownerName)
        if (!formData?.ownerName) {
            setFormData((prev) => ({ ...prev, toastMsg: "소유주 이름을 입력해주세요." }));
            return false;
        }
        // 3. 차량번호 (carNumber)
        if (!formData?.carNumber) {
            setFormData((prev) => ({ ...prev, toastMsg: "차량번호를 입력해주세요." }));
            return false;
        }
        // 4. 차종 (carGubun)
        if (!formData?.carGubun) {
            setFormData((prev) => ({ ...prev, toastMsg: "차종을 선택해주세요." }));
            return false;
        }
        // 5. 제조사 (bmNo)
        if (!formData?.bmNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "제조사를 입력해주세요." }));
            return false;
        }
        // 6. 모델 (boiNo)
        if (!formData?.boiNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "모델을 입력해주세요." }));
            return false;
        }
        // 7. 상세모델 (boNo)
        if (!formData?.boNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "상세모델을 입력해주세요." }));
            return false;
        }
        // 8. 등급 (bsNo)
        if (!formData?.bsNo) {
            setFormData((prev) => ({ ...prev, toastMsg: "등급을 선택해주세요." }));
            return false;
        }
        // 9. 최초등록일 (firstDate)
        if (!formData?.firstDate) {
            setFormData((prev) => ({ ...prev, toastMsg: "최초등록일을 입력해주세요." }));
            return false;
        }
        // 10. 색상 (color)
        if (!formData?.color) {
            setFormData((prev) => ({ ...prev, toastMsg: "색상을 입력해주세요." }));
            return false;
        }
        // 11. 유종 (fuel)
        if (!formData?.fuel) {
            setFormData((prev) => ({ ...prev, toastMsg: "유종을 선택해주세요." }));
            return false;
        }
        // 12. 변속기 (motorType)
        if (!formData?.motorType) {
            setFormData((prev) => ({ ...prev, toastMsg: "변속기를 선택해주세요." }));
            return false;
        }
        // 13. 배기량 (displacement)
        if (formData?.fuel !== "전기") {
            if (!formData?.displacement || Number(formData?.displacement) <= 0) {
                setFormData((prev) => ({ ...prev, toastMsg: "배기량을 입력해주세요." }));
                return false;
            }
        }
        // 14. 차대번호 (vin)
        // if (!formData?.vin ) {
        //     setFormData((prev) => ({ ...prev, toastMsg: "차대번호를 입력해주세요." }));
        //     return false;
        // }
        // 15. 보험 정보 (insurance)
        if (!formData?.insurance) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험 정보를 입력해주세요." }));
            return false;
        }
        if (!formData?.insurance.insuranceCorpId) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험사를 선택해주세요." }));
            return false;
        }
        if (!formData?.insurance.insMinAge) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험 연령을 입력해주세요." }));
            return false;
        }
        if (!formData?.insurance.expireDate) {
            setFormData((prev) => ({ ...prev, toastMsg: "보험 만료일을 설정해주세요." }));
            return false;
        }
        // 16. 자동차 등록증 (carUploads): carUploads 배열 내에 uploadType이 "REGI"인 항목이 존재해야 함.
        if (!formData?.carUploads || !Array.isArray(formData?.carUploads)) {
            setFormData((prev) => ({ ...prev, toastMsg: "자동차 등록증 파일을 첨부해주세요." }));
            return false;
        }
        const regiExists = formData?.carUploads.some((upload) => upload.uploadType === "REGI");
        if (!regiExists) {
            setFormData((prev) => ({ ...prev, toastMsg: "자동차 등록증 파일을 첨부해주세요." }));
            return false;
        }
        return true;
    };

    const handleSubmit = async (action) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = buildRequestBody();
            await action(requestBody);

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/vehicle/manage"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
                isLoading: false,
            }));
        }
    };

    // 차량 등록 시 호출 (CREATE)
    const handleSaveConfirm = async () => {
        await handleSubmit((requestBody) => postCar(requestBody).unwrap());
    };

    // 차량 수정 시 호출 (UPDATE)
    const handleUpdateConfirm = async () => {
        await handleSubmit((requestBody) => putCar({ carId: formData?.id, body: requestBody }).unwrap());
    };

    const toastPopup = (message) => {
        setFormData((prev) => ({
            ...prev,
            toastMsg: message,
        }));
    };

    const handleSave = () => {
        if (!validateCarRegistration()) return;

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `차량 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: mode === "CREATE" ? handleSaveConfirm : handleUpdateConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 옵션 추가 핸들러
    const handleAddOption = (optionName) => {
        if (!optionName.trim()) return;

        const newOption = {
            id: Date.now(), // 임시 ID
            name: optionName,
            price: null,
            updatedAt: null,
            createdAt: null,
            createdId: null,
            lastModifiedId: null,
        };

        setFormData((prev) => ({
            ...prev,
            options: [...prev.options, newOption],
        }));
    };

    useEffect(() => {
        if (vehicleId) {
            setFormData((prev) => ({
                ...prev,
                mode: "READ",
                id: vehicleId,
            }));
        }
    }, [vehicleId, setFormData]);

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "차량 등록" : "차량 상세"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode !== "CREATE" && (
                                /*
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    차량 삭제
                                </button>
                                 */
                                <></>
                            )}
                            {/* <button type="button" className="btn btn-outline" onClick={handleDelete}>
                                기본값 불러오기
                            </button> */}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사업자 선택</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <CorpInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기본 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <VehicleInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            handleSearchCar={handleSearchCar}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        옵션 정보 <i>(출고시 옵션)</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OptionInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            handleAddOption={handleAddOption}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">주차장 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ParkingLotInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">메모사항</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemoInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                    <div className="item-list">
                                        <InsuranceTable formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">여신 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <CapitalInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">첨부파일 등록</span>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ResultImage formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            parkingLotAddress: data.address,
                            parkingLotBCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

/* 기본 정보 */
const VehicleInfo = ({ formData, handleChange, setFormData, handleSearchCar }) => {
    const {
        ownerName,
        carNumber,
        vehicleOrigin,
        carGubun,
        manufacturer,
        model,
        detailModel,
        carClass,
        carTrim,
        fuel,
        transmission,
        motorType,
        color,
        displacement,
        mileage,
        newPrice,
        gasAmount,
        washStatus,
        vin,
        parkingLotBCode,
        bdName,
        bdNo,
        bmName,
        bmNo,
        boName,
        boNo,
        boiName,
        boiNo,
        branchId,
        bsName,
        bsNo,
    } = formData;

    // 차종 옵션 (독립)
    const { data: carGubunData } = useGetCarGubunQuery();
    const carGubunOptions =
        carGubunData && carGubunData.data
            ? carGubunData.data.map((item) => ({
                  value: item.bgNo,
                  label: item.bgName,
              }))
            : [];

    // 제조사
    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
        })) || [];

    // 모델 (skip: !manufacturer)
    const { data: modelData } = useGetCarModelQuery({ bmNo: bmNo }, { skip: !bmNo });
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
        })) || [];

    // 상세모델 (skip: !model)
    const { data: detailModelData } = useGetCarModelDetailQuery({ boiNo: boiNo }, { skip: !boiNo });
    const detailModelOptions =
        detailModelData?.data?.map((item) => ({
            value: item.boNo,
            label: item.boName,
        })) || [];

    // 등급 (skip: !detailModel)
    const { data: carClassData } = useGetCarClassQuery({ boNo: boNo }, { skip: !boNo });
    const carClassOptions =
        carClassData?.data?.map((item) => ({
            value: item.bsNo,
            label: item.bsName,
        })) || [];

    // 트림 (skip: !carClass)
    const { data: carTrimData } = useGetCarTrimQuery({ bsNo: bsNo }, { skip: !bsNo });
    const carTrimOptions =
        carTrimData?.data?.map((item) => ({
            value: item.bdNo,
            label: item.bdName,
        })) || [];

    // 유종 옵션 : getFuelList API
    const { data: fuelData } = useGetFuelListQuery();
    const fuelOptions =
        fuelData && fuelData.data
            ? fuelData.data.map((item) => ({
                  value: item.bfNo,
                  label: item.bfName,
              }))
            : [];

    // onChange 시 하위 필드를 초기화하는 helper
    const handleSelectChange = (field, option, clearFields = [], label) => {
        setFormData((prev) => ({
            ...prev,
            [field]: label || option.value,
            ...clearFields.reduce((acc, key) => {
                acc[key] = "";
                return acc;
            }, {}),
        }));
    };

    return (
        <>
            {/* 소유주 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        소유주 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="소유주 입력" value={ownerName || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 차량번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        차량번호 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="carNumber" placeholder="" value={carNumber || ""} onChange={handleChange} />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={handleSearchCar}
                            style={{ display: "none" }}
                        >
                            조회
                        </button>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>

            {/* 국산/수입 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>국산/수입</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={originOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="국산/수입 선택"
                            value={originOptions.find((opt) => opt.value === vehicleOrigin)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, vehicleOrigin: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 차종 (독립) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        차종 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            options={carGubunOptions}
                            isSearchable
                            placeholder="차종 선택"
                            value={carGubunOptions.find((opt) => opt.label === carGubun)}
                            onChange={(option) => handleSelectChange("carGubun", option, [], option.label)}
                        />
                    </div>
                </div>
            </div>

            {/* 제조사 (독립; 제조사 선택 시 하위 초기화) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        제조사 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="제조사 선택"
                            options={manufacturerOptions}
                            value={manufacturerOptions?.find((opt) => opt.value === bmNo)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    bmNo: option.value,
                                    boiNo: undefined,
                                    boNo: undefined,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 모델 (제조사 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        모델 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="모델 선택"
                            options={modelOptions}
                            value={modelOptions?.find((opt) => opt.value === boiNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    boiNo: option.value,
                                    boNo: undefined,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 상세모델 (모델 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        상세모델 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="상세모델 선택"
                            options={detailModelOptions}
                            value={detailModelOptions?.find((opt) => opt.value === boNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    boNo: option.value,
                                    bsNo: undefined,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 등급 (상세모델 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>등급</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="등급 선택"
                            options={carClassOptions}
                            value={carClassOptions?.find((opt) => opt.value === bsNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bsNo: option.value,
                                    bdNo: undefined,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 트림 (등급 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>트림</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="트림 선택"
                            options={carTrimOptions}
                            value={carTrimOptions?.find((opt) => opt.value === bdNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bdNo: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 최초등록일 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        최초등록일 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {/* <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={formData?.firstDate}
                            onChange={(date) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    firstDate: date,
                                }));
                            }}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                        /> */}
                        <input
                            type="text"
                            name="firstDate"
                            value={formatDate(formData?.firstDate)}
                            placeholder="ex) 20250101"
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            {/* 외장색상 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        외장색상 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="예) 화이트" value={color || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 유종 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        유종 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유종 선택"
                            value={fuelOptions.find((opt) => opt.label === fuel)}
                            onChange={(option) => {
                                const isElectric = option.label === "전기";
                                setFormData((prev) => ({
                                    ...prev,
                                    fuel: option.label,
                                    displacement: isElectric ? "" : prev.displacement,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 변속기 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        변속기 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={transmissionOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="변속기 선택"
                            value={transmissionOptions.find((opt) => opt.value === motorType)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, motorType: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 배기량 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배기량(cc)</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="배기량 선택"
                            options={displacementOptions}
                            value={displacementOptions?.find((opt) => opt.value === displacement)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    displacement: option.value,
                                }))
                            }
                            isDisabled={formData?.fuel === "전기"}
                        />
                    </div>
                </div>
            </div>

            {/* 주행거리 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주행거리(km)</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="mileage"
                            placeholder=""
                            value={mileage || ""}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">km</span>
                    </div>
                </div>
            </div>

            {/* 유류량 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>유류량</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유류량 선택"
                            value={fuelRateOptions.find((opt) => opt.value === gasAmount)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, gasAmount: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 세차상태 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>세차상태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={washStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="세차상태 선택"
                            value={washStatusOptions.find((opt) => opt.value === washStatus)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, washStatus: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 신차출고가 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>신차출고가</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="newPrice"
                            placeholder=""
                            value={newPrice || ""}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>

            {/* 차대번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차대번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="vin" placeholder="" value={vin || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 검사 유효기간 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>검사 유효기간</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="inspectRangeFrom"
                            value={formatDate(formData?.inspectRangeFrom)}
                            placeholder="ex) 20250101"
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                    ~
                    <div className="form-element">
                        <input
                            type="text"
                            name="inspectRangeTo"
                            value={formatDate(formData?.inspectRangeTo)}
                            placeholder="ex) 20251231"
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const CorpInfo = ({ formData, handleChange, setFormData }) => {
    const { rentCorpId, branchId, carLocationId, carStatus } = formData;

    // API 호출: 나의 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: branchData } = useGetBranchInfoQuery(rentCorpId, {
        skip: !rentCorpId,
    });
    const branchOptions =
        branchData && branchData.data
            ? branchData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: carLocationData } = useGetCarLocationListQuery();
    const carLocationOptions =
        carLocationData && carLocationData.data
            ? carLocationData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            {/* 사업자 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        사업자 선택 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 선택"
                            value={corpOptions.find((opt) => opt.value === rentCorpId)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, rentCorpId: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 지점 선택 */}
            {/* <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>지점 선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={branchOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="지점 선택"
                            value={branchOptions.find((opt) => opt.value === branchId)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, branchId: option.value }))}
                        />
                    </div>
                </div>
            </div> */}

            {/* 등록 차고지 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>등록 차고지 </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={carLocationOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="등록 차고지"
                            value={carLocationOptions.find((opt) => opt.value === carLocationId)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, carLocationId: option.value }))}
                        />
                    </div>
                </div>
            </div>

            {/* 차량 상태 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        차량 상태 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={vehicleStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="차량 상태"
                            value={vehicleStatusOptions.find((opt) => opt.value === carStatus)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, carStatus: option.value }))}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const OptionInfo = ({ formData, handleChange, setFormData, handleAddOption }) => {
    const { options } = formData;
    const [optionName, setOptionName] = useState("");

    const addOption = () => {
        if (optionName.trim() === "") {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "옵션명을 입력해주세요",
            }));
            return;
        }
        handleAddOption(optionName);
        setOptionName("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            addOption();
        }
    };
    return (
        <>
            {/* 옵션 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>옵션 추가</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            value={optionName}
                            placeholder="옵션명"
                            onChange={(e) => setOptionName(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={addOption}>
                            추가
                        </button>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>선택옵션</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="option-list">
                            {options?.map((item, idx) => (
                                <div className="option-item" key={idx}>
                                    <span className="option-tit">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="item-row" data-grow="1">
                <div className="row-content">
                    <div className="form-element">
                        <span className="content-desc">등록된 서비스 항목이 없습니다.</span>
                    </div>
                </div>
            </div> */}
        </>
    );
};

const ParkingLotInfo = ({ formData, handleChange, setFormData }) => {
    const { parkingLotStatus, parkingLotId, parkingLotName } = formData;

    const { data: parkingLotData } = useGetParkingLotListQuery();
    const parkingLotOptions =
        parkingLotData && parkingLotData.data
            ? parkingLotData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            {/* 주차장 구분 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주차장 구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_DIRECT"
                                checked={parkingLotStatus === "DIRECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "DIRECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_DIRECT">직접입력</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_SELECT"
                                checked={parkingLotStatus === "SELECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "SELECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_SELECT">주차장 선택</label>
                        </div>
                    </div>
                </div>
            </div>
            {/* 사용 주차장 */}
            {parkingLotStatus === "SELECT" && (
                <>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>사용 주차장</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <Select
                                    options={parkingLotOptions}
                                    className="custom-select"
                                    classNamePrefix="custom-select"
                                    isSearchable={false}
                                    placeholder="등록 주차장"
                                    value={parkingLotOptions.find((opt) => opt.value === parkingLotId)}
                                    onChange={(option) => setFormData((prev) => ({ ...prev, parkingLotId: option.value }))}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                </>
            )}
            {/* 주차장 주소 */}
            {parkingLotStatus === "DIRECT" && (
                <>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>주차장 이름</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="parkingLotName"
                                    value={parkingLotName}
                                    placeholder="주차장 이름"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-element">
                                <div className="checkbox">
                                    <input
                                        type="checkbox"
                                        id="parkingLotCheck"
                                        name="parkingLotCheck"
                                        checked={formData?.parkingLotAddYn}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                parkingLotAddYn: e.target.checked,
                                            }))
                                        }
                                    />
                                    <label htmlFor="parkingLotCheck">
                                        <span>주차장 목록에 추가</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="1">
                        <div className="row-head">
                            <span>주차장</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element" style={{ width: "200px" }}>
                                <input
                                    type="text"
                                    placeholder=""
                                    name="parkingLotBCode"
                                    value={formData?.parkingLotBCode}
                                    onChange={handleChange}
                                    readOnly
                                    onClick={() => {
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            addressPopupStatus: true,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="form-element" style={{ width: "668px" }}>
                                <input
                                    type="text"
                                    placeholder=""
                                    name="parkingLotAddress"
                                    value={formData?.parkingLotAddress}
                                    onChange={handleChange}
                                    readOnly
                                    onClick={() => {
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            addressPopupStatus: true,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="form-element">
                                <input
                                    type="text"
                                    placeholder="상세 주소를 입력해주세요."
                                    name="parkingLotAddressDetail"
                                    value={formData?.parkingLotAddressDetail}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-element_button">
                                <button
                                    type="button"
                                    className="btn-form_element btn-outline"
                                    onClick={() => {
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            addressPopupStatus: true,
                                        }));
                                    }}
                                >
                                    주소검색
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const MemoInfo = ({ formData, handleChange, setFormData }) => {
    const { memo } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="메모를 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 보험 정보 */
const InsuranceInfo = ({ formData, handleChange, setFormData }) => {
    const { insurance } = formData;

    // API 호출: 보험사 업체 조회
    const { data: insuranceCorpData } = useGetInsuranceCorpQuery();
    const insuranceCorpOptions =
        insuranceCorpData && insuranceCorpData.data
            ? insuranceCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            {/* 보험사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        보험사 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="보험사 선택"
                            value={insuranceCorpOptions.find((opt) => opt.value === insurance?.insuranceCorpId)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    insurance: {
                                        ...prev.insurance,
                                        insuranceCorpId: option.value,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 보험연령 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        보험연령 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceAgeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="보험연령"
                            value={insuranceAgeOptions.find((opt) => opt.value === insurance?.insMinAge)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    insurance: {
                                        ...prev.insurance,
                                        insMinAge: option.value,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 만료일 설정 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        만료일 설정 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {/* <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={insurance?.expireDate}
                            onChange={(date) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    insurance: {
                                        ...prev.insurance,
                                        expireDate: dateFormatDateTime(date, "YYYY-MM-DD"),
                                    },
                                }));
                            }}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                        /> */}
                        <input
                            type="text"
                            name="insurance.expireDate"
                            value={formatDate(insurance?.expireDate)}
                            placeholder="ex) 20250101"
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            {/* 보험 가입 금액 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>보험 가입 금액</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="insurance.regInsurancePrice"
                            value={insurance?.regInsurancePrice}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
        </>
    );
};

/* 보험 정보 테이블 */
const InsuranceTable = ({ formData, handleChange, setFormData }) => {
    const insurance = formData?.insurance ?? {};
    const insuranceItems = insurance?.insuranceItems ?? [];
    const [selectAll, setSelectAll] = useState(false);

    // 초기 선택 상태 확인
    useEffect(() => {
        if (insuranceItems.length > 0) {
            const allSelected = insuranceItems.every((item) => item.joinYn === "Y");
            setSelectAll(allSelected);
        }
    }, [insuranceItems]);

    // 특정 item 업데이트 헬퍼
    const updateItem = (index, newValues) => {
        setFormData((prev) => {
            const newInsuranceItems = [...prev.insurance?.insuranceItems];
            newInsuranceItems[index] = {
                ...newInsuranceItems[index],
                ...newValues,
            };
            return {
                ...prev,
                insurance: {
                    ...prev.insurance,
                    insuranceItems: newInsuranceItems,
                },
            };
        });
    };

    // joinYn 토글 핸들러
    const handleToggleJoinYn = (index, currentJoinYn) => {
        const nextJoinYn = currentJoinYn === "Y" ? "N" : "Y";
        updateItem(index, { joinYn: nextJoinYn });

        // 모든 항목이 선택되었는지 확인
        const allSelected = insuranceItems.every((item) => item.joinYn === nextJoinYn);
        setSelectAll(allSelected);
    };

    // 전체 선택 토글 핸들러
    const handleToggleAll = () => {
        const nextSelectAll = !selectAll;
        setSelectAll(nextSelectAll);

        // 모든 항목의 joinYn을 업데이트
        setFormData((prev) => ({
            ...prev,
            insurance: {
                ...prev.insurance,
                insuranceItems: prev.insurance.insuranceItems.map((item) => ({
                    ...item,
                    joinYn: nextSelectAll ? "Y" : "N",
                })),
            },
        }));
    };

    return (
        <>
            <div className="table-container">
                <div className="table-content">
                    <div className="table-wrapper">
                        <div className="table">
                            <table>
                                <colgroup>
                                    <col width="25%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="cell">
                                                <span
                                                    className="cell-tit"
                                                    style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}
                                                >
                                                    가입 여부{" "}
                                                    <button
                                                        type="button"
                                                        className={`btn-toggle-own ${selectAll ? "active" : ""}`}
                                                        onClick={handleToggleAll}
                                                    />
                                                </span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">종류</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">보상범위</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">자부담금</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {insuranceItems.map((item, idx) => {
                                        const config = INSURANCE_ITEM_CONFIGS[item.insuranceItemType];
                                        if (!config) {
                                            return null;
                                        }

                                        return (
                                            <tr key={item.insuranceItemType}>
                                                {/* 가입 여부 버튼 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <button
                                                            type="button"
                                                            className={`btn-toggle-own ${item.joinYn === "Y" ? "active" : ""}`}
                                                            onClick={() => handleToggleJoinYn(idx, item.joinYn)}
                                                        />
                                                    </div>
                                                </td>

                                                {/* 보험 종류 라벨 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-desc color-main">{config.label}</span>
                                                    </div>
                                                </td>

                                                {/* 보상범위 Select 영역 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-input">
                                                            {/* 
                                                                1) "자기차량손해"일 때는 Select가 2개.
                                                                2) "대인배상1" ~ 기타 일반 항목은 Select 1개 or limitType 직접 처리 
                                                            */}
                                                            {item.insuranceItemType === "SELF_CAR_DAMAGE" ? (
                                                                // 자기차량손해: 첫 번째 select(금액/비율), 두 번째 select(옵션)
                                                                <>
                                                                    {/* 첫 번째 select: 금액 / 비율 */}
                                                                    <Select
                                                                        className="custom-select"
                                                                        classNamePrefix="custom-select"
                                                                        isSearchable={false}
                                                                        placeholder="구분"
                                                                        // 현재 선택값(금액? 비율?)을 어디에 저장할지 정의 필요
                                                                        // 예: compensationRangeType: "PRICE" or "RATE"
                                                                        value={
                                                                            item.selfCarDamageRangeType
                                                                                ? selfCarDamageTypeOptions.find(
                                                                                      (opt) => opt.value === item.selfCarDamageRangeType
                                                                                  )
                                                                                : null
                                                                        }
                                                                        onChange={(option) => {
                                                                            // selfCarDamageRangeType 필드에 "PRICE"/"RATE" 저장
                                                                            updateItem(idx, {
                                                                                selfCarDamageRangeType: option.value, // "PRICE" / "RATE"
                                                                                compensationRange: null, // 보상범위 초기화
                                                                            });
                                                                        }}
                                                                        options={selfCarDamageTypeOptions}
                                                                        isDisabled={item.joinYn !== "Y"}
                                                                    />

                                                                    {/* 두 번째 select: item.selfCarDamageRangeType === 'PRICE'면 금액 옵션, 'RATE'면 비율 옵션 */}
                                                                    {item.selfCarDamageRangeType === "PRICE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="금액"
                                                                            value={
                                                                                item.compensationRange
                                                                                    ? selfCarDamageAmountOptions.find(
                                                                                          (opt) => opt.value === item.compensationRange
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                updateItem(idx, {
                                                                                    compensationRange: option.value,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageAmountOptions}
                                                                            isDisabled={item.joinYn !== "Y"}
                                                                        />
                                                                    )}

                                                                    {item.selfCarDamageRangeType === "RATE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="비율"
                                                                            value={
                                                                                item.selfCarDamageRateType
                                                                                    ? selfCarDamageRateOptions.find(
                                                                                          (opt) => opt.value === item.selfCarDamageRateType
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                updateItem(idx, {
                                                                                    selfCarDamageRateType: option.value,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageRateOptions}
                                                                            isDisabled={item.joinYn !== "Y"}
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : item.insuranceItemType === "DAEIN_PAY_1" ? (
                                                                // 대인배상1은 limitType만 존재(자배법 규정 한도)라면, 고정값 Select or read-only
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions} // daeinPay1Options
                                                                    placeholder="보상범위"
                                                                    // coverageOptions[0].value => "LAW_MAX"
                                                                    value={
                                                                        item.limitType
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.limitType
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType:
                                                                                typeof option.value === "string" ? option.value : null,
                                                                            compensationRange:
                                                                                typeof option.value === "number" ? option.value : null,
                                                                        });
                                                                    }}
                                                                    isDisabled={item.joinYn !== "Y"}
                                                                />
                                                            ) : item.insuranceItemType === "DAEIN_PAY_2" ? (
                                                                // 대인배상2 => 무한 or 금액
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions} // daeinPay2Options
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.limitType === "UN_LIMIT"
                                                                            ? config.coverageOptions.find((opt) => opt.value === "UN_LIMIT")
                                                                            : config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                    }
                                                                    onChange={(option) => {
                                                                        if (option.value === "UN_LIMIT") {
                                                                            updateItem(idx, {
                                                                                limitType: "UN_LIMIT",
                                                                                compensationRange: null,
                                                                            });
                                                                        } else {
                                                                            updateItem(idx, {
                                                                                limitType: null,
                                                                                compensationRange: Number(option.value),
                                                                            });
                                                                        }
                                                                    }}
                                                                    isDisabled={item.joinYn !== "Y"}
                                                                />
                                                            ) : item.insuranceItemType === "EMERGENCY_SERVICE" ? (
                                                                // 긴급출동
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions} // emergencyServiceOptions
                                                                    placeholder="연 횟수"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: option.value, // 3,5,6
                                                                        });
                                                                    }}
                                                                    isDisabled={item.joinYn !== "Y"}
                                                                />
                                                            ) : (
                                                                // 그 외 (대물배상, 자동차상해(사망/부상/장해), 무보험차상해 등)
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: Number(option.value),
                                                                        });
                                                                    }}
                                                                    isDisabled={item.joinYn !== "Y"}
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* 자부담금 입력 */}
                                                <td>
                                                    <div className="cell" data-align="left">
                                                        <span className="cell-input">
                                                            <input
                                                                type="number"
                                                                placeholder="0"
                                                                value={item.selfPay || ""}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    updateItem(idx, {
                                                                        selfPay: val ? Number(val) : null,
                                                                    });
                                                                }}
                                                                disabled={item.joinYn !== "Y"}
                                                            />
                                                            <span className="input-esset">
                                                                {item.insuranceItemType === "EMERGENCY_SERVICE" ? "km" : "원"}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* 캐피탈 정보 */
const CapitalInfo = ({ formData, handleChange, setFormData }) => {
    const { capital } = formData;

    const updateCapital = (newValues) => {
        setFormData((prev) => ({
            ...prev,
            capital: {
                ...prev.capital,
                ...newValues,
            },
        }));
    };

    return (
        <>
            {/* 캐피탈 업체 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>캐피탈 업체</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={capitalCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="캐피탈 업체"
                            value={capitalCorpOptions.find((opt) => opt.value === capital?.capitalCorpName)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    capital: {
                                        ...prev.capital,
                                        capitalCorpName: option.value,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 선수 금액 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>선수 금액</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="capital.preChargeFee"
                            value={capital?.preChargeFee}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>

            {/* 대출 총액 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대출 총액</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="capital.loanTotalAmount"
                            value={capital?.loanTotalAmount}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>

            {/* 적용 금리 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>적용 금리</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="capital.loanInterestRate"
                            value={capital?.loanInterestRate}
                            onChange={handleChange}
                            onInput={formatDecimalInput}
                        />
                        <span className="input-esset">%</span>
                    </div>
                </div>
            </div>

            {/* 할부 기간 (월) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>할부 기간</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={contractMonthOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="할부 기간"
                            value={contractMonthOptions.find((opt) => opt.value === capital?.installmentRange)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    capital: {
                                        ...prev.capital,
                                        installmentRange: option.value,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 월 할부금 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>월 할부금</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="capital.installmentMonthlyFee"
                            value={capital?.installmentMonthlyFee}
                            onChange={handleChange}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>
        </>
    );
};

// 첨부파일 등록
const ResultImage = ({ formData, setFormData }) => {
    const getFileName = (uploadType) => {
        const uploadItem = formData?.carUploads?.find((item) => item.uploadType === uploadType);
        if (uploadItem && uploadItem.upload && uploadItem.upload.fileName) {
            return uploadItem.upload.fileName;
        }
        if (uploadType === "REGI") return formData?.regiFileName || "";
        if (uploadType === "INSURANCE") return formData?.insuranceFileName || "";
        if (uploadType === "ESTIMATE") return formData?.estimateFileName || "";
        return "";
    };

    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

    // 화면 표시용 파일명
    const { regiFileName, insuranceFileName, estimateFileName } = formData;

    // 숨겨진 input ref
    const regiFileRef = useRef(null);
    const insFileRef = useRef(null);
    const estFileRef = useRef(null);

    // 파일첨부 버튼 → ref.click()
    const handleButtonClick = (type) => {
        if (type === "REGI") {
            regiFileRef.current.click();
        } else if (type === "INSURANCE") {
            insFileRef.current.click();
        } else if (type === "ESTIMATE") {
            estFileRef.current.click();
        }
    };

    // 업로드 처리
    const handleFileChange = async (e, uploadType) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);

            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;

            setFormData((prev) => {
                // carUploads 업데이트
                const updated = [...(prev.carUploads || [])];
                const idx = updated.findIndex((x) => x.uploadType === uploadType);
                if (idx >= 0) {
                    updated[idx] = { ...updated[idx], uploadId: newUploadId };
                } else {
                    updated.push({ uploadId: newUploadId, uploadType });
                }
                // 화면 표시용 파일명
                let newState = {};
                if (uploadType === "REGI") {
                    newState = { regiFileName: fileObj.name };
                } else if (uploadType === "INSURANCE") {
                    newState = { insuranceFileName: fileObj.name };
                } else if (uploadType === "ESTIMATE") {
                    newState = { estimateFileName: fileObj.name };
                }
                return {
                    ...prev,
                    carUploads: updated,
                    ...newState,
                };
            });
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    return (
        <>
            {/* 1) 자동차등록증 (uploadType = "REGI") */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>
                        자동차 등록증 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("REGI")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.carUploads?.find((item) => item.uploadType === "REGI");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("REGI");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("REGI")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={regiFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "REGI")}
                        />
                    </div>
                </div>
            </div>

            {/* 2) 보험가입증 (uploadType = "INSURANCE") */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>보험가입증</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("INSURANCE")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.carUploads?.find((item) => item.uploadType === "INSURANCE");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("INSURANCE");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("INSURANCE")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={insFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "INSURANCE")}
                        />
                    </div>
                </div>
            </div>

            {/* 3) 구매견적서 (uploadType = "ESTIMATE") */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>구매견적서</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("ESTIMATE")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.carUploads?.find((item) => item.uploadType === "ESTIMATE");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("ESTIMATE");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("ESTIMATE")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={estFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "ESTIMATE")}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="2"></div>
        </>
    );
};
